/**
 * The anchored tooltip (redesign §26, rule 8): ink #1D1D1F, white 12.5 / 1.4,
 * radius 8, padding 6 × 10, at most 260 wide, no arrow, 8 from its trigger —
 * above and centred, below when it would leave by the top, shifted to stay 8
 * from the window's sides. It shows 400 ms after the mouse arrives, at once on
 * a keyboard focus, and at once when another tooltip closed less than 300 ms
 * ago (a row of icons). Escape closes it. Never interactive, layer 60,
 * `role="tooltip"`, named by `aria-describedby` on the trigger while shown.
 *
 * It replaces the `title` attribute for a held button's reason and for the
 * exact time behind a date cell: a title waits a second, cannot be reached
 * from the keyboard, and is drawn by the system in its own colours.
 */
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type React from 'react';
import { createPortal } from 'react-dom';
import { usePresence } from '../lib/presence';

const GAP = 8;
const EDGE = 8;
let lastClosed = 0;

export interface TooltipTrigger<T extends HTMLElement> {
  ref: (el: T | null) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onFocus: () => void;
  onBlur: () => void;
  'aria-describedby'?: string;
}

/**
 * Props to spread on the trigger and the tooltip to render beside it. With no
 * `text` the trigger gets nothing but its ref and the tooltip never shows.
 */
export function useTooltip<T extends HTMLElement>(
  text?: string,
  /** `start` aligns the bubble's left edge on its anchor's — for a control at the left of its column (§40.8). */
  align: 'center' | 'start' = 'center'
): { trigger: TooltipTrigger<T>; tip: React.ReactNode } {
  const id = useId();
  const anchor = useRef<T | null>(null);
  const timer = useRef(0);
  const [shown, setShown] = useState(false);

  const show = useCallback(
    (now: boolean) => {
      if (!text) return;
      window.clearTimeout(timer.current);
      if (now || performance.now() - lastClosed < 300) setShown(true);
      else timer.current = window.setTimeout(() => setShown(true), 400);
    },
    [text]
  );
  const hide = useCallback(() => {
    window.clearTimeout(timer.current);
    setShown((was) => {
      if (was) lastClosed = performance.now();
      return false;
    });
  }, []);

  useEffect(() => () => window.clearTimeout(timer.current), []);
  useEffect(() => {
    if (!shown) return;
    const key = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') hide();
    };
    document.addEventListener('keydown', key);
    window.addEventListener('scroll', hide, true);
    return () => {
      document.removeEventListener('keydown', key);
      window.removeEventListener('scroll', hide, true);
    };
  }, [shown, hide]);

  const trigger: TooltipTrigger<T> = {
    ref: (el) => {
      anchor.current = el;
    },
    onMouseEnter: () => show(false),
    onMouseLeave: hide,
    onFocus: () => {
      if (anchor.current?.matches(':focus-visible')) show(true);
    },
    onBlur: hide,
    'aria-describedby': shown && text ? id : undefined
  };
  return { trigger, tip: text ? <TooltipBubble id={id} text={text} open={shown} anchor={anchor} align={align} /> : null };
}

function TooltipBubble({
  id,
  text,
  open,
  anchor,
  align
}: {
  id: string;
  text: string;
  open: boolean;
  anchor: React.RefObject<HTMLElement | null>;
  align: 'center' | 'start';
}): React.JSX.Element | null {
  const { mounted, ref } = usePresence<HTMLDivElement>(open, { o: 0, y: 4 }, { d: 1, r: 0.3 }, { o: 0 });
  const box = useRef<HTMLDivElement | null>(null);
  const [place, setPlace] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!mounted || !open || !box.current || !anchor.current) return;
    const at = anchor.current.getBoundingClientRect();
    const { width, height } = box.current.getBoundingClientRect();
    const above = at.top - GAP - height;
    const top = above < EDGE ? at.bottom + GAP : above;
    const wanted = align === 'start' ? at.left : at.left + at.width / 2 - width / 2;
    const left = Math.max(EDGE, Math.min(wanted, window.innerWidth - width - EDGE));
    setPlace({ top, left });
  }, [mounted, open, text, anchor, align]);

  if (!mounted) return null;
  return createPortal(
    <div
      id={id}
      role="tooltip"
      ref={(el) => {
        box.current = el;
        ref(el);
      }}
      style={{
        position: 'fixed',
        top: place?.top ?? -9999,
        left: place?.left ?? -9999,
        zIndex: 60,
        maxWidth: 260,
        padding: '6px 10px',
        borderRadius: 8,
        background: '#1D1D1F',
        color: '#FFFFFF',
        fontSize: 12.5,
        lineHeight: 1.4,
        fontWeight: 400,
        textAlign: 'left',
        whiteSpace: 'normal',
        pointerEvents: 'none'
      }}
    >
      {text}
    </div>,
    document.body
  );
}

/** A focusable span carrying a tooltip, for text that is not itself a control (a date cell). */
export function WithTooltip({
  text,
  children,
  style
}: {
  text: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}): React.JSX.Element {
  const { trigger, tip } = useTooltip<HTMLSpanElement>(text);
  return (
    <>
      <span tabIndex={0} {...trigger} style={style}>
        {children}
      </span>
      {tip}
    </>
  );
}
